import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import cart from 'domains/cart';

import actions from './cart_shipping.actions';

import type { Address, BaseAddress } from 'domains/addresses';
import type { CheckoutStatus } from 'domains/checkout';
import type { ShippingMethodType } from 'domains/shipping_methods';
import type { CartPickupMethod, CartShippingMethod } from './cart_shipping.types';
import type { StrictUnion } from 'types/common_types';

type CartShippingState = {
  type: ShippingMethodType;
  saved: StrictUnion<CartShippingMethod | CartPickupMethod> | null;
  addressStatus: CheckoutStatus;
  methodStatus: CheckoutStatus;
  originalAddress?: Address;
  suggestedAddress?: BaseAddress;
  confirmedAddress?: Address;
  validationStatus: Exclude<CheckoutStatus, 'error'>;
};

const initialState: CartShippingState = {
  type: 'standard',
  saved: null,
  addressStatus: 'idle',
  methodStatus: 'idle',
  validationStatus: 'idle',
};

const slice = createSlice({
  name: 'cartShipping',
  initialState,
  reducers: {
    resetStatus(state) {
      return { ...state, addressStatus: 'idle', methodStatus: 'idle' };
    },
    setShipType(state, { payload }: PayloadAction<ShippingMethodType>) {
      return {
        ...state,
        type: payload,
      };
    },
    resetPendingShippingAddress(state) {
      return {
        ...state,
        originalAddress: undefined,
        suggestedAddress: undefined,
        confirmedAddress: undefined,
        validationStatus: 'idle',
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(cart.actions.get.fulfilled, (state, { payload }) => {
        return { ...state, saved: payload.shipping };
      })
      .addCase(actions.validateShippingAddress.pending, (state, { payload }) => {
        return {
          ...state,
          validationStatus: 'pending',
          originalAddress: payload,
        };
      })
      .addCase(actions.validateShippingAddress.fulfilled, (state, { payload }) => {
        return {
          ...state,
          validationStatus: 'success',
          suggestedAddress: payload,
        };
      })
      .addCase(actions.validateShippingAddress.confirm, (state, { payload }) => {
        // Really should not be possible, but clarify it for type checking
        if (!state.originalAddress) return state;

        let confirmedAddress: Address | undefined;

        if (payload === 'original') {
          confirmedAddress = { ...state.originalAddress };
        }

        if (payload === 'suggested' && state.suggestedAddress) {
          confirmedAddress = {
            ...state.suggestedAddress,
            name: state.originalAddress.name,
            phone: state.originalAddress.phone,
          };
        }

        return {
          ...state,
          confirmedAddress,
          originalAddress: undefined,
          suggestedAddress: undefined,
          validationStatus: 'idle',
        };
      })
      .addCase(actions.validateShippingAddress.cancel, (state) => {
        return {
          ...state,
          originalAddress: undefined,
          suggestedAddress: undefined,
          confirmedAddress: undefined,
          validationStatus: 'idle',
        };
      })
      .addCase(actions.saveShippingAddress.pending, (state) => {
        return { ...state, addressStatus: 'pending' };
      })
      .addCase(actions.saveShippingAddress.fulfilled, (state) => {
        return {
          ...state,
          addressStatus: 'success',
          confirmedAddress: undefined,
          originalAddress: undefined,
          suggestedAddress: undefined,
        };
      })
      .addCase(actions.saveShippingMethod.pending, (state) => {
        return { ...state, addressStatus: 'pending', methodStatus: 'pending' };
      })
      .addCase(actions.saveShippingMethod.fulfilled, (state) => {
        return {
          ...state,
          addressStatus: 'success',
          methodStatus: 'success',
          confirmedAddress: undefined,
          originalAddress: undefined,
          suggestedAddress: undefined,
        };
      })
      .addCase(actions.saveShippingMethod.rejected, (state) => {
        return { ...state, methodStatus: 'error' };
      });
  },
});

export default slice;
